import React, { FC, useState, useMemo, useCallback, useEffect } from 'react'
import { IntlProvider } from 'react-intl'
import { ThemeProvider } from 'styled-components'

import { InvisibleSmartCaptcha } from '@yandex/smart-captcha'
import { theme } from '@elamajs/ui-kit'
import { Input, CheckBox, Link, Label, Button, Select } from '@iui-kit/components/new'
import { ajax } from '@src/utils/ajax'
import { uniqueId } from 'lodash'

import { useRecaptchaSitekey } from '../../hooks'
import { selectRoleOptions } from '../../constants'
import { validation } from '../../helpers/validation'

import { PLACE_TO_RENDER, VARIANT_TO_RENDER, INDEX_REQUEST } from '../../constants'

import messageIcon from '../../assets/message.svg'

import * as Styled from './IndexFormInner.styled'

type FormData = {
  name: string
  email: string
  role?: string
  anotherRole: string
  adsSendingPermission: boolean
  indexRequest: string
  'g-recaptcha-response'?: string
}

type Props = {
  renderPlace: string
  elementId: string
  variant?: string
}

type ErrorList = {
  name: string
  email: string
  role: string
  anotherRole: string
}

const optionOther = 'other'

const initialFormData: FormData = {
  name: '',
  email: '',
  anotherRole: '',
  adsSendingPermission: false,
  indexRequest: ''
}

const initialErrorList: ErrorList = {
  name: '',
  email: '',
  role: '',
  anotherRole: ''
}

const IndexFormInner: FC<Props> = ({ renderPlace, elementId, variant }) => {
  const [data, setData] = useState<FormData>(initialFormData)
  const [error, setError] = useState('')
  const [errorList, setErrorList] = useState<ErrorList>(initialErrorList)
  const [isFetching, setIsFetching] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [token, setToken] = useState('')
  const [captchaKey, setCaptchaKey] = useState(0)
  const [visible, setVisible] = useState(false)
  const recaptchaSitekey = useRecaptchaSitekey({ elementId })

  const handleCheckErrors = useCallback((): ErrorList => {
    const errors: ErrorList = {
      name: validation.name(data.name),
      email: validation.email(data.email),
      role: validation.role(data?.role),
      anotherRole: validation.anotherRole(data.anotherRole, data?.role)
    }

    setErrorList(errors)

    return errors
  }, [data.anotherRole, data.email, data.name, data.role])

  const handleChange = useCallback(
    (value: string, e: React.ChangeEvent<HTMLInputElement> | string) => {
      let name: string

      if (typeof e === 'string') {
        name = e
      } else {
        name = e.target.name
      }

      setData(prevState => ({
        ...prevState,
        [name]: value
      }))

      if (errorList[name as keyof ErrorList]) {
        setErrorList(prevState => ({
          ...prevState,
          [name]: ''
        }))
      }
    },
    [errorList]
  )

  const handleSelectChange = useCallback(
    (value: string) => {
      setData(prevState => ({
        ...prevState,
        role: value,
        anotherRole: value === optionOther ? prevState.anotherRole : ''
      }))

      if (errorList.role) {
        setErrorList(prevState => ({
          ...prevState,
          role: ''
        }))
      }

      if (value !== optionOther && errorList.anotherRole) {
        setErrorList(prevState => ({
          ...prevState,
          anotherRole: ''
        }))
      }
    },
    [errorList.anotherRole, errorList.role]
  )

  const buttonText = useMemo(() => {
    return renderPlace === PLACE_TO_RENDER.MODAL ? 'Получить данные' : 'Подписаться на рассылку'
  }, [renderPlace])

  const indexRequest = useMemo(() => {
    if (renderPlace === PLACE_TO_RENDER.FORM) return INDEX_REQUEST.SUBSCRIBE
    else {
      switch (variant) {
        case VARIANT_TO_RENDER.REALTY:
          return INDEX_REQUEST.REALESTATE
        case VARIANT_TO_RENDER.STATISTICS:
          return INDEX_REQUEST.DAAI
        case VARIANT_TO_RENDER.EDTECH:
          return INDEX_REQUEST.EDTECH
        case VARIANT_TO_RENDER.MEDICINE:
          return INDEX_REQUEST.MEDICAL
        default:
          return INDEX_REQUEST.SUBSCRIBE
      }
    }
  }, [renderPlace, variant])

  const handleCheckboxChange = useCallback((checked: boolean) => {
    setData(prevState => ({
      ...prevState,
      adsSendingPermission: checked
    }))
  }, [])

  const handleTokenSuccess = useCallback((token: string) => {
    setToken(token)
  }, [])

  const handleChallengeHidden = useCallback(() => setVisible(false), [])

  const handleSubmit = useCallback(() => {
    const errors = handleCheckErrors()
    const hasErrors = Object.values(errors).some(error => error !== '')

    if (hasErrors) {
      return
    }

    setVisible(true)
    setIsFetching(true)
  }, [handleCheckErrors])

  const handleFormSubmit = useCallback(() => {
    const resultData = { ...data, indexRequest }
    resultData['g-recaptcha-response'] = token

    ajax
      .post('/info/api/elama-index/send', JSON.stringify(resultData))
      .then(() => {
        setData(initialFormData as FormData)
        setIsSuccess(true)
      })
      .catch(error => {
        if (error) {
          setError('Не удалось отправить заявку, попробуйте еще раз')
        }
      })
      .finally(() => {
        setIsFetching(false)
        setVisible(false)
        setCaptchaKey(uniqueId())
        setToken('')
      })
  }, [data, token])

  useEffect(() => {
    if (token && isFetching) {
      handleFormSubmit()
    }
  }, [token, isFetching])

  const showAnotherRole = useMemo(() => data.role === optionOther, [data.role])

  return (
    <Styled.Form $renderPlace={renderPlace}>
      <Styled.Block $renderPlace={renderPlace}>
        <Input
          onChange={handleChange}
          value={data.name}
          name="name"
          maxLength={30}
          minLength={2}
          placeholder="Имя"
          errorMessage={errorList.name}
        />
        <Input
          onChange={handleChange}
          value={data.email}
          name="email"
          placeholder="Email"
          errorMessage={errorList.email}
        />
        <Styled.LabelWrap>
          <Label label="Ваша профессиональная роль" />
          <Styled.Select
            as={Select}
            value={data.role}
            options={selectRoleOptions}
            label="role"
            onChange={handleSelectChange}
            placeholder="Выберите вариант"
            errorMessage={errorList.role}
            optionsWidth={290}
          />
        </Styled.LabelWrap>
        {showAnotherRole && (
          <Input
            onChange={handleChange}
            value={data.anotherRole}
            name="anotherRole"
            maxLength={30}
            minLength={2}
            placeholder="Ваша профессиональная роль"
            errorMessage={errorList.anotherRole}
          />
        )}
        <CheckBox
          onChange={handleCheckboxChange}
          name="adsSendingPermission"
          checked={data.adsSendingPermission}
          label={
            <>
              Даю{' '}
              <Link href="https://elama.ru/legal_docs/event/consent_information_communication.pdf">
                согласие
              </Link>{' '}
              на получение рекламных сообщений и&nbsp;иных материалов рекламного характера
            </>
          }
        />
      </Styled.Block>
      <Styled.Footer>
        {isSuccess ? (
          <Styled.ButtonSuccess>
            <img src={messageIcon} width={20} height={20} alt="message" />
            Заявка отправлена
          </Styled.ButtonSuccess>
        ) : (
          <Styled.Button
            as={Button}
            isLoading={isFetching}
            onClick={handleSubmit}
            disabled={isFetching}
          >
            {buttonText}
          </Styled.Button>
        )}
        {error && <Styled.Error>{error}</Styled.Error>}
        <Styled.Text>
          Нажимая кнопку, вы даете согласие{' '}
          <Link href="https://elama.ru/legal_docs/pd/consent_agency.pdf">
            на&nbsp;обработку персональных данных{' '}
          </Link>
        </Styled.Text>
      </Styled.Footer>
      <Styled.CaptchaWrapper>
        <InvisibleSmartCaptcha
          sitekey={recaptchaSitekey}
          key={captchaKey}
          visible={visible}
          shieldPosition="bottom-left"
          onChallengeHidden={handleChallengeHidden}
          onSuccess={handleTokenSuccess}
        />
      </Styled.CaptchaWrapper>
    </Styled.Form>
  )
}

const withProviders = (Component: FC<Props>): FC<Props> => {
  const NewComponent: FC<Props> = props => {
    return (
      <ThemeProvider theme={theme}>
        <IntlProvider locale="ru">
          <Component {...props} />
        </IntlProvider>
      </ThemeProvider>
    )
  }

  return NewComponent
}
export default withProviders(IndexFormInner)
